import { PropsWithChildren, ReactNode, useCallback, useEffect, useState } from 'react'

import { useAuth } from '@apps-common/contexts'
import { useDevSettingsStore } from '@apps-common/stores'

export const AUTH_CLAIMS = {
  portalUser: {
    roles: ['radionetPortalMobileApp.user'],
    scopes: ['radionetPortalMobileAppApi']
  },
  portalAdmin: {
    roles: ['radionetPortalMobileApp.user', 'radionetPortalMobileApp.admin'],
    scopes: ['radionetPortalMobileAppApi']
  },
  managementUser: {
    roles: ['radionetManagement.user'],
    scopes: ['radionetManagementApi']
  },
  managementAdmin: {
    roles: ['radionetManagement.user', 'radionetManagement.admin'],
    scopes: ['radionetManagementApi']
  },
  apiUser: {
    roles: [''],
    scopes: ['']
  },
  apiAdmin: {
    roles: [''],
    scopes: ['']
  },
  myMonitoringUser: {
    roles: ['radionetMyMonitoring.user'],
    scopes: ['radionetPortalApi']
  },
  myMonitoringAdmin: {
    roles: ['radionetMyMonitoring.user', 'radionetMyMonitoring.admin'],
    scopes: ['radionetPortalApi']
  },
  myMonitoringManagementAdmin: {
    roles: ['radionetMyMonitoring.user', 'radionetMyMonitoring.admin', 'radionetMyMonitoring.management'],
    scopes: ['radionetPortalApi', 'radionetManagementApi']
  }
}

export const useAuthorization = () => {
  const { user } = useAuth()

  const checkAccess = useCallback(
    ({ roles, scopes, devRoles, devScopes }: { roles: string[]; scopes: string[]; devRoles?: string[]; devScopes?: string[] }) => {
      if (roles && scopes) {
        const userRoles: any = devRoles || user?.idTokenResult.claims.role || []
        const userScopes: any = devScopes || user?.idTokenResult.claims.scope || []

        let roleRes = false
        let scopeRes = false

        if (userRoles) {
          roleRes = roles.every((role) => userRoles.includes(role))
        }

        if (userScopes) {
          scopeRes = scopes.every((scope) => userScopes.includes(scope))
        }

        return roleRes && scopeRes
      }

      return false
    },
    [user?.idTokenResult.claims]
  )

  return { checkAccess }
}

export const useAuthorizationCheckAccess = ({ roles = [], scopes = [] }: { roles?: string[]; scopes?: string[] }) => {
  const { checkAccess } = useAuthorization()

  return checkAccess({ roles, scopes })
}

type AuthorizationProps = {
  notAuthorizedFallback?: ReactNode
} & {
  roles?: string[]
  scopes?: string[]
  ignoreDev?: boolean
}

export const Authorization = ({
  roles = [],
  scopes = [],
  notAuthorizedFallback = null,
  ignoreDev = false,
  children
}: PropsWithChildren<AuthorizationProps>) => {
  const [devPermissions, setDevPermissions] = useState<any>()
  const { permissions } = useDevSettingsStore()
  const { checkAccess } = useAuthorization()

  useEffect(() => {
    if (ignoreDev) {
      setDevPermissions(null)
    } else if (permissions === 'portalUser') {
      setDevPermissions(AUTH_CLAIMS.portalUser)
    } else if (permissions === 'portalAdmin') {
      setDevPermissions(AUTH_CLAIMS.portalAdmin)
    } else if (permissions === 'managementUser') {
      setDevPermissions(AUTH_CLAIMS.managementUser)
    } else if (permissions === 'managementAdmin') {
      setDevPermissions(AUTH_CLAIMS.managementAdmin)
    } else if (permissions === 'apiUser') {
      setDevPermissions(AUTH_CLAIMS.apiUser)
    } else if (permissions === 'apiAdmin') {
      setDevPermissions(AUTH_CLAIMS.apiUser)
    } else {
      setDevPermissions(null)
    }
  }, [permissions, ignoreDev])

  const canAccess = checkAccess({ roles, scopes, devRoles: devPermissions?.roles, devScopes: devPermissions?.scopes })

  // console.log(canAccess, roles, scopes, devPermissions)

  const component = canAccess ? children : notAuthorizedFallback

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{component}</>
}

export const useCheckAuthorization = ({ roles = [], scopes = [], ignoreDev = false }: PropsWithChildren<AuthorizationProps>) => {
  const [devPermissions, setDevPermissions] = useState<any>()
  const { permissions } = useDevSettingsStore()
  const { checkAccess } = useAuthorization()

  useEffect(() => {
    if (ignoreDev) {
      setDevPermissions(null)
    } else if (permissions === 'portalUser') {
      setDevPermissions(AUTH_CLAIMS.portalUser)
    } else if (permissions === 'portalAdmin') {
      setDevPermissions(AUTH_CLAIMS.portalAdmin)
    } else if (permissions === 'managementUser') {
      setDevPermissions(AUTH_CLAIMS.managementUser)
    } else if (permissions === 'managementAdmin') {
      setDevPermissions(AUTH_CLAIMS.managementAdmin)
    } else if (permissions === 'apiUser') {
      setDevPermissions(AUTH_CLAIMS.apiUser)
    } else if (permissions === 'apiAdmin') {
      setDevPermissions(AUTH_CLAIMS.apiUser)
    } else {
      setDevPermissions(null)
    }
  }, [permissions, ignoreDev])

  const canAccess = checkAccess({ roles, scopes, devRoles: devPermissions?.roles, devScopes: devPermissions?.scopes })

  return canAccess
}
